import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { useScreenContext } from '@/components/SteppedContent/context/ScreenContext';
import SteppedDialog from '@/components/SteppedDialog';
import type { HTMLMotionProps } from 'motion/react';

/*
 * Modal Screen component
 * This component is used to define a screen within a modal, it is used to
 * register the screen with the ScreenContext and conditionally render the
 * screen based on the current screen.
 * @param {string} screenName - The name of the screen
 * @param {HTMLMotionProps<'div'>} props - Props to pass to the motion.div component
 * @returns {JSX.Element} Screen component
 * @example
 * <Modal.Screen screenName="screen-1">Screen 1</Modal.Screen>
 * @see /src/components/SteppedDialog/index.tsx
 */
const Screen = ({
  screenName,
  children,
  ref,
  ...props
}: {
  screenName: string;
} & HTMLMotionProps<'div'>) => {
  const {
    currentScreen,
    registerScreen
  } = useScreenContext();
  useEffect(() => {
    registerScreen(screenName);
  }, [screenName, registerScreen]);
  const isVisible = currentScreen === screenName;
  return <AnimatePresence initial={false} mode="popLayout" data-sentry-element="AnimatePresence" data-sentry-component="Screen" data-sentry-source-file="index.tsx">
      {isVisible && <motion.div data-dialog-screen={screenName} data-state={isVisible ? 'open' : 'closed'} key={screenName} {...props} ref={typeof ref === 'function' ? ref : null}>
          {children}
        </motion.div>}
    </AnimatePresence>;
};
const Modal = {
  ...SteppedDialog,
  Screen
};
export default Modal;